import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { fetchOtherApplications, setOtherCurrentPage } from '../../../../store/slices/Tuition/tuitionManagerPortalSlice';
import CardTable from '../../../../components/common/CardTable'
import Table from '../../../../components/common/Table/Table'
import Pagination from '../../../../components/common/Table/Pagination'
import ScrollToTopBtn from '../../../../components/common/ScrollToTopBtn'

interface OtherApplicationsProps {
  applicationData?: any;
}

const OtherApplications: React.FC<OtherApplicationsProps> = ({ applicationData }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { state } = location;
  const empNuid = state?.empNuid || applicationData?.empNuid || null; // Safely retrieve empNuid from state or props

  const otherState = useAppSelector(
    (state) => state?.tuitionManagerPortal?.otherApplications
  )
  const { applications = [], loading = false, error = null, currentPage = 1, perPage = 10, totalItems = 0 } = otherState || {}

  useEffect(() => {
    if (empNuid) {
      dispatch(fetchOtherApplications({ page: currentPage, per_page: perPage, empNuid }));
    }
  }, [empNuid, currentPage, perPage, dispatch]);

  const handlePageChange = (page: number) => {
    dispatch(setOtherCurrentPage(page));
  };

  interface TableColumn {
    key: string
    label: string
    className?: string
  }

  interface TableRow {
    [key: string]: React.ReactNode
  }

  const columns: TableColumn[] = [
    { key: 'empNuid', label: 'Employee ID' },
    { key: 'empName', label: 'Name' },
    { key: 'id', label: 'Application #' },
    { key: 'applicationDate', label: 'Application date' },
    { key: 'status', label: 'Status' },
    { key: 'approveDate', label: 'Approval Date' },
  ]

  const rows: TableRow[] = applications.map((r: { status: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | null | undefined }) => ({
    ...r,
    status: (
      <span className="bg-gray-100 border border-[#00000014] rounded px-3! py-1 text-sm font-semibold inline-flex items-center justify-center">
        {r.status}
      </span>
    ),
  }))

  if (error) {
    return (
      <div className="page-container">
        <CardTable>
          <div className="w-full flex items-center justify-center min-h-[400px]">
            <div className="w-full max-w-md">
              <div className="flex flex-col items-center text-center">
                <h3 className="h3 text-red-600 mb-2">Error</h3>
                <p className="text-gray-700 text-sm">{error}</p>
              </div>
            </div>
          </div>
        </CardTable>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="page-container">
        <CardTable>
          <div className="flex items-center justify-center min-h-[400px]">
            <p>Loading...</p>
          </div>
        </CardTable>
      </div>
    )
  }

  if (applications.length === 0) {
    return (
      <div className="page-container">
        <CardTable>
          <div className="w-full flex items-center justify-center min-h-[400px]">
            <div className="w-full max-w-md">
              <div className="flex flex-col items-center text-center">
                <h3 className="h3 text-gray-600 mb-2">No applications found</h3>
                <p className="text-gray-700 text-sm">There are no other applications for this employee.</p>
              </div>
            </div>
          </div>
        </CardTable>
      </div>
    )
  }

  return (
    <div className="page-container">
      <CardTable>
        <Table
          columns={columns}
          rows={rows}
          caption="Selected employee's other applications"
          isSorting={false}
        />
      </CardTable>

      <div className="mt-4 mobile:relative mobile:left-[30px]">
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems || applications.length}
          itemsPerPage={perPage}
          onPageChange={handlePageChange}
        />
      </div>

      <ScrollToTopBtn />
    </div>
  );
};

export default OtherApplications;
